import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author = 'Importerr',
  publishedTime,
  modifiedTime,
  product,
  canonicalUrl,
  noindex = false,
  nofollow = false,
  structuredData,
}) => {
  const siteUrl = window.location.origin;
  const currentUrl = url || window.location.href;
  const canonical = canonicalUrl || currentUrl;
  
  // Default values
  const defaultTitle = 'Importerr - Premium Dropshipping Services | Source Products from China';
  const defaultDescription = 'Importerr offers exceptional dropshipping services connecting you with quality products from trusted suppliers. Start your dropshipping business today with competitive pricing and reliable shipping.';
  const defaultImage = `${siteUrl}/images/importerr-logo.png`;
  const defaultKeywords = 'dropshipping, alibaba products, wholesale, china sourcing, product sourcing, ecommerce, dropshipping services, importerr';

  const metaTitle = title || defaultTitle;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  const metaKeywords = keywords || defaultKeywords;

  // Robots meta content
  const robotsContent = `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;

  // Generate structured data for products
  const generateProductStructuredData = () => {
    if (!product) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      image: product.images || [metaImage],
      brand: {
        '@type': 'Brand',
        name: product.brand || 'Importerr',
      },
      offers: {
        '@type': 'Offer',
        url: currentUrl,
        priceCurrency: product.currency || 'INR',
        price: product.price,
        availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        priceValidUntil: product.priceValidUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      },
      aggregateRating: product.rating ? {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviewCount || 1,
      } : undefined,
    };
  };

  // Generate structured data for organization
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Importerr',
    url: siteUrl,
    logo: defaultImage,
    description: defaultDescription,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'support@importerr.com',
    },
    sameAs: [
      'https://www.facebook.com/importerr',
      'https://www.twitter.com/importerr',
      'https://www.linkedin.com/company/importerr',
      'https://www.instagram.com/importerr',
    ],
  };

  // Generate breadcrumb structured data
  const generateBreadcrumbStructuredData = (breadcrumbs) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: `${siteUrl}${crumb.url}`,
      })),
    };
  };

  const finalStructuredData = structuredData || generateProductStructuredData() || organizationStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Importerr" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:site" content="@importerr" />
      <meta name="twitter:creator" content="@importerr" />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Published and Modified Times */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Product-specific Open Graph tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content={product.currency || 'INR'} />
          <meta property="product:availability" content={product.inStock ? 'in stock' : 'out of stock'} />
          {product.brand && <meta property="product:brand" content={product.brand} />}
          {product.category && <meta property="product:category" content={product.category} />}
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://cdn.jsdelivr.net" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://cdn.jsdelivr.net" />
    </Helmet>
  );
};

export default SEO;

